/*
 * Iter 31.303 (D1) — Agent Registry admin panel.
 *
 * Adds/edits/deactivates AI agent labels in the data-driven registry
 * (db.agent_registry). Built-ins (claude/gemini/copilot/chatgpt) are
 * badge-marked and can only be deactivated, never hard-deleted, so
 * historical audit trails remain resolvable.
 *
 * Registry drives the Multi-Agent Tokens mint dropdown — adding an
 * agent here immediately unlocks token minting for it, no rebuild.
 */
import React, { useCallback, useEffect, useReducer } from "react";
import { api } from "@/lib/api";
import {
    Loader2, AlertTriangle, Plus, Users, Trash2, ShieldCheck,
    CheckCircle2, PowerOff,
} from "lucide-react";

const INITIAL = {
    agents: [], loading: true, err: null,
    formKey: "", formLabel: "", formDesc: "",
    creating: false, createErr: null,
    busyKey: null,
};

function reducer(s, a) {
    switch (a.type) {
        case "load_ok": return { ...s, agents: a.agents, loading: false, err: null };
        case "load_err": return { ...s, loading: false, err: a.err };
        case "form_key": return { ...s, formKey: a.v.toLowerCase().replace(/[^a-z0-9_-]/g, "") };
        case "form_label": return { ...s, formLabel: a.v };
        case "form_desc": return { ...s, formDesc: a.v };
        case "form_reset": return { ...s, formKey: "", formLabel: "", formDesc: "", createErr: null };
        case "create_busy": return { ...s, creating: true, createErr: null };
        case "create_err": return { ...s, creating: false, createErr: a.err };
        case "create_ok": return { ...s, creating: false, createErr: null, formKey: "", formLabel: "", formDesc: "" };
        case "busy": return { ...s, busyKey: a.key };
        case "busy_done": return { ...s, busyKey: null };
        default: return s;
    }
}

export default function AgentRegistryPanel() {
    const [s, dispatch] = useReducer(reducer, INITIAL);

    const reload = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/agents");
            dispatch({ type: "load_ok", agents: data?.agents || [] });
        } catch (e) {
            dispatch({
                type: "load_err",
                err: `[HTTP ${e.response?.status || "?"}] ${e.response?.data?.detail || e.message}`,
            });
        }
    }, []);

    useEffect(() => { reload(); }, [reload]);

    const create = useCallback(async () => {
        if (!s.formKey || !s.formLabel) return;
        dispatch({ type: "create_busy" });
        try {
            await api.post("/admin/agents", {
                key: s.formKey,
                label: s.formLabel,
                description: s.formDesc || undefined,
            });
            dispatch({ type: "create_ok" });
            reload();
        } catch (e) {
            dispatch({
                type: "create_err",
                err: `[HTTP ${e.response?.status || "?"}] ${e.response?.data?.detail || e.message}`,
            });
        }
    }, [s.formKey, s.formLabel, s.formDesc, reload]);

    const toggle = useCallback(async (key, next) => {
        dispatch({ type: "busy", key });
        try {
            await api.patch(`/admin/agents/${encodeURIComponent(key)}`, { active: next });
            reload();
        } catch (_) { /* swallow — reload will re-sync */ }
        finally { dispatch({ type: "busy_done" }); }
    }, [reload]);

    const removeAgent = useCallback(async (key) => {
        if (!window.confirm(
            `Delete agent "${key}"?\n\n` +
            "Built-ins refuse. If any tokens are still active on this " +
            "label the delete is rejected — revoke them first via the " +
            "Multi-Agent Tokens tile."
        )) return;
        dispatch({ type: "busy", key });
        try {
            await api.delete(`/admin/agents/${encodeURIComponent(key)}`);
            reload();
        } catch (e) {
            window.alert(
                `Delete failed: ${e.response?.data?.detail || e.message}`
            );
        } finally { dispatch({ type: "busy_done" }); }
    }, [reload]);

    return (
        <div
            className="paper-card border-2 border-ink/30 p-5 mb-6"
            data-testid="agent-registry-panel"
        >
            <div className="flex items-center justify-between mb-3">
                <h2 className="font-heading uppercase tracking-[0.22em] text-sm text-ink flex items-center gap-2">
                    <Users className="w-4 h-4 text-stamp-red" />
                    Agent Registry
                </h2>
                <button
                    type="button"
                    onClick={reload}
                    disabled={s.loading}
                    data-testid="ar-refresh"
                    className="font-heading uppercase tracking-[0.16em] text-[10px] text-ink-soft hover:text-stamp-red disabled:opacity-50"
                >
                    {s.loading ? <Loader2 className="w-3 h-3 animate-spin inline" /> : "refresh"}
                </button>
            </div>

            <div className="font-body text-[11px] text-ink-soft mb-3 leading-relaxed">
                Iter 31.303 — data-driven agent list. Add new AI agents here to unlock token minting for them (no rebuild). Built-ins can be deactivated (hides from mint dropdown, preserves audit trail) but not hard-deleted.
            </div>

            {s.err ? (
                <div className="mb-3 label-typed text-[11px] leading-relaxed border-l-2 border-stamp-red pl-3 text-stamp-red flex items-start gap-1" data-testid="ar-load-err">
                    <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                    <span>{s.err}</span>
                </div>
            ) : null}

            {/* Agent list */}
            <div className="border border-ink/20 mb-4 divide-y divide-ink/15" data-testid="ar-list">
                {s.loading && s.agents.length === 0 ? (
                    <div className="p-3 font-body text-[11px] text-ink-faint italic" data-testid="ar-loading">Loading…</div>
                ) : s.agents.length === 0 ? (
                    <div className="p-3 font-body text-[11px] text-ink-faint italic">No agents registered.</div>
                ) : (
                    s.agents.map((a) => (
                        <div
                            key={a.key}
                            className="flex items-start justify-between gap-3 p-3"
                            data-testid={`ar-agent-${a.key}`}
                        >
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                    <ShieldCheck className={`w-3.5 h-3.5 ${a.active ? "text-emerald-700" : "text-ink-faint"}`} />
                                    <code className="font-mono text-[11px] text-ink font-bold">{a.key}</code>
                                    <span className="font-body text-[11px] text-ink-soft">·</span>
                                    <span className="font-body text-[11px] text-ink">{a.label}</span>
                                    {a.is_builtin ? (
                                        <span className="inline-block px-1.5 py-0.5 border border-ink/30 text-[9px] font-heading uppercase tracking-[0.18em] text-ink-soft">
                                            builtin
                                        </span>
                                    ) : null}
                                    <span className={`inline-block px-1.5 py-0.5 border text-[9px] font-heading uppercase tracking-[0.18em] ${a.active ? "text-emerald-700 border-emerald-700" : "text-ink-faint border-ink/30"}`}>
                                        {a.active ? "active" : "inactive"}
                                    </span>
                                </div>
                                {a.description ? (
                                    <div className="pl-5 font-body text-[10px] text-ink-soft leading-relaxed">
                                        {a.description}
                                    </div>
                                ) : null}
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                                <button
                                    type="button"
                                    onClick={() => toggle(a.key, !a.active)}
                                    disabled={s.busyKey === a.key}
                                    data-testid={`ar-toggle-${a.key}`}
                                    className="font-heading uppercase text-[9px] tracking-[0.18em] px-2 py-1 border border-ink/30 text-ink-soft hover:text-stamp-red hover:border-stamp-red disabled:opacity-50 inline-flex items-center gap-1"
                                    title={a.active ? "Deactivate" : "Reactivate"}
                                >
                                    {s.busyKey === a.key ? <Loader2 className="w-3 h-3 animate-spin" /> : (a.active ? <PowerOff className="w-3 h-3" /> : <CheckCircle2 className="w-3 h-3" />)}
                                    {a.active ? "deactivate" : "activate"}
                                </button>
                                {!a.is_builtin ? (
                                    <button
                                        type="button"
                                        onClick={() => removeAgent(a.key)}
                                        disabled={s.busyKey === a.key}
                                        data-testid={`ar-delete-${a.key}`}
                                        className="font-heading uppercase text-[9px] tracking-[0.18em] px-2 py-1 border border-ink/30 text-ink-soft hover:text-stamp-red hover:border-stamp-red disabled:opacity-50 inline-flex items-center gap-1"
                                        title="Hard-delete (only if no active tokens)"
                                    >
                                        <Trash2 className="w-3 h-3" />
                                        delete
                                    </button>
                                ) : null}
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Create form */}
            <div className="border border-ink/20 p-3" data-testid="ar-create-form">
                <div className="label-typed text-[10px] text-ink mb-2 flex items-center gap-2">
                    <Plus className="w-3 h-3" />
                    Register new agent
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
                    <input
                        type="text"
                        placeholder="key · lowercase-kebab"
                        value={s.formKey}
                        onChange={(e) => dispatch({ type: "form_key", v: e.target.value })}
                        data-testid="ar-form-key"
                        className="px-2 py-1.5 border border-ink/30 bg-paper-light font-mono text-[11px] text-ink"
                        maxLength={32}
                    />
                    <input
                        type="text"
                        placeholder="Display label"
                        value={s.formLabel}
                        onChange={(e) => dispatch({ type: "form_label", v: e.target.value })}
                        data-testid="ar-form-label"
                        className="px-2 py-1.5 border border-ink/30 bg-paper-light font-mono text-[11px] text-ink"
                        maxLength={80}
                    />
                    <input
                        type="text"
                        placeholder="Description (optional)"
                        value={s.formDesc}
                        onChange={(e) => dispatch({ type: "form_desc", v: e.target.value })}
                        data-testid="ar-form-desc"
                        className="px-2 py-1.5 border border-ink/30 bg-paper-light font-mono text-[11px] text-ink"
                        maxLength={500}
                    />
                </div>
                {s.createErr ? (
                    <div className="text-[11px] text-stamp-red font-body mb-2" data-testid="ar-create-err">
                        {s.createErr}
                    </div>
                ) : null}
                <button
                    type="button"
                    onClick={create}
                    disabled={s.creating || !s.formKey || !s.formLabel}
                    data-testid="ar-create-submit"
                    className="font-heading uppercase text-[10px] tracking-[0.22em] px-4 py-2 border-2 border-stamp-red text-stamp-red hover:bg-stamp-red/10 disabled:opacity-40 inline-flex items-center gap-1.5"
                >
                    {s.creating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                    Register agent
                </button>
            </div>
        </div>
    );
}
